import { bind, classNames, Component, createRef, h, signal, tag } from "omi";
import isEmpty from "lodash-es/isEmpty";

import { NWGraphic } from "@/wc/components/atoms/nw-graphic";
import { createLoginStore, LoginState } from "@/wc/pages/login/store.ts";
import { AUTH_LOGGED_IN_TOAST_TRIGGER } from "@/globals/storageKeys.ts";
import { animateCss } from "@/globals/styles";
import { AuthService } from "@/services/authService";
import { addToQueryString, getReturnTo } from "@/utils/url.ts";
import { NWButton, NWDivider } from "@/wc/components";
import { tailwind } from "@/tailwind";
import { isEmail, isMobileDevice, isWebView } from "@/utils";

import { NWOtpForm } from "./features/otpForm";
import { NWTestimonialCarousel } from "./features/testimonialCarousel";

@tag("pages-login")
export class LoginPage extends Component {
  static css = [tailwind, animateCss];

  private authService: AuthService | undefined;

  private passwordlessState = createLoginStore();
  private emailForm = createRef<HTMLFormElement>();
  private isWebView = signal(isWebView());

  install() {
    this.authService = new AuthService();
  }

  @bind
  private async handleEmailSubmit(e?: Event) {
    e?.preventDefault();

    const isValid = this.emailForm.current?.reportValidity();
    if (!isValid) {
      return;
    }

    this.passwordlessState.error.value = undefined;
    await this.handlePasswordlessLogin(this.passwordlessState.email.value);
  }

  @bind
  private async handleOTPOnSubmit(e?: Event) {
    e?.preventDefault();
    this.passwordlessState.error.value = undefined;

    await this.handlePasswordlessVerfication(
      this.passwordlessState.email.value,
      this.passwordlessState.verificationCode.value,
    );
  }

  @bind
  private async handlePasswordlessLogin(email: string) {
    if (this.passwordlessState.email.value == null || !isEmail(this.passwordlessState.email.value)) {
      this.passwordlessState.error.value = "Invalid email. Please try again.";
      return;
    }

    this.passwordlessState.isLoading.value = true;
    const wasSuccessful = await this.authService?.startPasswordlessLogin(email);
    if (wasSuccessful) {
      this.passwordlessState.state.value = LoginState.OTP_VERIFICATION;
    } else {
      this.passwordlessState.error.value = "Something went wrong. Please try again.";
    }

    this.passwordlessState.isLoading.value = false;
  }

  @bind
  private async handlePasswordlessVerfication(email: string, code: string) {
    this.passwordlessState.isLoading.value = true;
    const wasSuccessful = await this.authService?.verifyPasswordlessCode(email, code);
    if (wasSuccessful) {
      addToQueryString(AUTH_LOGGED_IN_TOAST_TRIGGER, "true");
      window.location.reload();
    } else {
      this.passwordlessState.error.value = "That code was invalid. Please try again.";
    }

    this.passwordlessState.isLoading.value = false;
  }

  @bind
  private goBackAStep() {
    this.passwordlessState.state.value = LoginState.INITIAL;
    this.passwordlessState.error.value = undefined;
  }

  @bind
  private renderOTP() {
    const error = this.passwordlessState.error.value;

    return (
      <div class="flex flex-col items-center space-y-4 animate__animated animate__slideInRight">
        <div class="bg-brand-50 rounded-lg w-full px-4 py-2">
          <p class="text-tertiary text-sm text-center">An email with the code has been sent!</p>
        </div>

        <div class="w-full space-y-6">
          <NWOtpForm
            error={error}
            onNotReceived={this.goBackAStep}
            onChange={(code: string) => {
              this.passwordlessState.verificationCode.value = code;

              const submitButtonContainer = this.shadowRoot?.querySelector("#otp-submit");
              const submitButton = submitButtonContainer?.shadowRoot?.querySelector("button");
              if (submitButton) {
                // Ensures that the button is focused after the code is entered, and it's disabled state has been toggled
                setTimeout(() => (submitButton as HTMLButtonElement).focus(), 0);
              }
            }}
          />

          <div class="w-full mt-6">
            <NWButton
              id="otp-submit"
              size="full"
              type="primary"
              loading={this.passwordlessState.isLoading.value}
              disabled={isEmpty(this.passwordlessState.verificationCode.value)}
              onClick={this.handleOTPOnSubmit}
            >
              Let's learn!
            </NWButton>
          </div>

          <div class="">
            <NWButton size="full" type="text" icon="arrow-left" onClick={this.goBackAStep}>
              Go back
            </NWButton>
          </div>
        </div>
      </div>
    );
  }

  @bind
  private renderInitialState() {
    const showGoogleSSO = !this.isWebView.value;
    const error = this.passwordlessState.error.value;
    const returnTo = getReturnTo({ [AUTH_LOGGED_IN_TOAST_TRIGGER]: "true" });

    return (
      <div class="min-w-max">
        {showGoogleSSO && (
          <>
            <NWButton size="full" type="secondary" icon="google" href={`/auth/google?returnTo=${returnTo}`}>
              Continue with Google
            </NWButton>

            <NWDivider padding="xs">
              <span>OR</span>
            </NWDivider>
          </>
        )}

        <form ref={this.emailForm} onSubmit={this.handleEmailSubmit} class="w-full">
          <div class="mb-4">
            <input
              required
              type="email"
              placeholder={"Enter your email"}
              onInputCapture={e => (this.passwordlessState.email.value = (e.target as HTMLInputElement).value)}
              class={classNames(
                "w-full transition-all text-primary focus:outline-none border px-3.5 py-2.5 rounded-lg bg-white shadow-xs",
                {
                  "border-error-300  border-2 focus:shadow-xse focus:border-error-600": !!error,
                  "border-gray-300 focus:shadow-xss focus:border-brand-400": !error,
                },
              )}
            />
            {error && <p class="text-sm text-error-600 mt-1.5">{error}</p>}
          </div>

          <NWButton size="full" loading={this.passwordlessState.isLoading.value} onClick={this.handleEmailSubmit}>
            Let's learn!
          </NWButton>
        </form>
      </div>
    );
  }

  @bind
  renderFormState() {
    switch (this.passwordlessState.state.value) {
      default:
      case LoginState.INITIAL:
        return this.renderInitialState();

      case LoginState.OTP_VERIFICATION:
        return this.renderOTP();
    }
  }

  render() {
    return (
      <div class="bg-white rounded-xl flex flex-row h-[680px] w-full text justify-between max-h-3/4 z-[100] md:p-6 border border-gray-200 shadow-xl">
        <div class="left w-full lg:w-2/3 xl:w-1/2 flex flex-col justify-center items-center">
          <div class="flex flex-col items-center space-y-3">
            <NWGraphic mode="static" name="hello" />
            {isMobileDevice() ? (
              <div class="flex items-center flex-col">
                <h3 class="font-semibold">Sign in to unlock all projects</h3>
                <p class="text-tertiary">It's free - no card required!</p>
              </div>
            ) : (
              <h3 class="text-tertiary">Log in or sign up in seconds (it’s free!)</h3>
            )}
          </div>

          <div class="my-8 w-full px-4 sm:px-8 md:px-12 max-w-lg">
            <div class="space-y-4">{this.renderFormState()}</div>
          </div>
        </div>

        <div class="right hidden lg:block max-w-2xl lg:w-4/5 xl:1/2 w-full">
          <NWTestimonialCarousel />
        </div>
      </div>
    );
  }
}
